/**
 * Client-side attempt throttling for auth-sensitive flows.
 *
 * Scope and limits: this is a UX-level brake, not a security boundary. It runs
 * in the browser and a determined attacker can clear localStorage or script the
 * backend directly. The real ceiling is the auth provider's own built-in rate
 * limits (sign-in attempts, OTP sends, auth emails per hour). This layer stops
 * casual brute force, accidental hammering, and naive bot loops, and gives the
 * person a clear "wait N minutes" message instead of a wall of red errors.
 *
 * Policy: 5 failures inside a 15 minute window, then a 15 minute block.
 */

export const THROTTLE_MAX_ATTEMPTS = 5;
export const THROTTLE_WINDOW_MS = 15 * 60 * 1000;
export const THROTTLE_BLOCK_MS = 15 * 60 * 1000;

export type ThrottleAction =
  | "sign_in"
  | "sign_up"
  | "password_reset"
  | "invite_code"
  | "phone_otp_send"
  | "phone_otp_verify";

type Record_ = { failures: number[]; blockedUntil: number };

const PREFIX = "myelina.throttle.";

/** Fallback when localStorage is unavailable (private mode, blocked storage). */
const memory = new Map<string, string>();

function readRaw(key: string): string | null {
  try {
    return window.localStorage.getItem(key);
  } catch {
    return memory.get(key) ?? null;
  }
}

function writeRaw(key: string, value: string | null) {
  try {
    if (value === null) window.localStorage.removeItem(key);
    else window.localStorage.setItem(key, value);
  } catch {
    if (value === null) memory.delete(key);
    else memory.set(key, value);
  }
}

/**
 * Identity is only used to bucket attempts locally. It is lowercased and
 * truncated so we never persist a full credential-looking string.
 */
function storageKey(action: ThrottleAction, identity?: string): string {
  const id = (identity ?? "").trim().toLowerCase().slice(0, 120);
  return `${PREFIX}${action}${id ? `.${id}` : ""}`;
}

function load(key: string, now: number): Record_ {
  const raw = readRaw(key);
  if (!raw) return { failures: [], blockedUntil: 0 };
  try {
    const parsed = JSON.parse(raw) as Partial<Record_>;
    const failures = Array.isArray(parsed.failures)
      ? parsed.failures.filter((t): t is number => typeof t === "number" && now - t < THROTTLE_WINDOW_MS)
      : [];
    const blockedUntil = typeof parsed.blockedUntil === "number" && parsed.blockedUntil > now ? parsed.blockedUntil : 0;
    return { failures, blockedUntil };
  } catch {
    return { failures: [], blockedUntil: 0 };
  }
}

function save(key: string, record: Record_) {
  if (record.failures.length === 0 && record.blockedUntil === 0) {
    writeRaw(key, null);
    return;
  }
  writeRaw(key, JSON.stringify(record));
}

export type ThrottleState = {
  /** False when the action is currently blocked. */
  allowed: boolean;
  /** Seconds until the block lifts (0 when allowed). */
  retryAfterSeconds: number;
  /** Attempts left in the current window before a block kicks in. */
  remaining: number;
};

/** Check whether an action may proceed right now. Does not record anything. */
export function checkThrottle(action: ThrottleAction, identity?: string, now = Date.now()): ThrottleState {
  const key = storageKey(action, identity);
  const record = load(key, now);
  if (record.blockedUntil > now) {
    return {
      allowed: false,
      retryAfterSeconds: Math.ceil((record.blockedUntil - now) / 1000),
      remaining: 0,
    };
  }
  return {
    allowed: true,
    retryAfterSeconds: 0,
    remaining: Math.max(0, THROTTLE_MAX_ATTEMPTS - record.failures.length),
  };
}

/** Record one failed attempt. Returns the state after recording. */
export function recordFailure(action: ThrottleAction, identity?: string, now = Date.now()): ThrottleState {
  const key = storageKey(action, identity);
  const record = load(key, now);
  record.failures.push(now);

  if (record.failures.length >= THROTTLE_MAX_ATTEMPTS) {
    record.blockedUntil = now + THROTTLE_BLOCK_MS;
    record.failures = [];
    save(key, record);
    return { allowed: false, retryAfterSeconds: Math.ceil(THROTTLE_BLOCK_MS / 1000), remaining: 0 };
  }

  save(key, record);
  return {
    allowed: true,
    retryAfterSeconds: 0,
    remaining: THROTTLE_MAX_ATTEMPTS - record.failures.length,
  };
}

/** Clear the counter after a successful attempt. */
export function clearThrottle(action: ThrottleAction, identity?: string) {
  writeRaw(storageKey(action, identity), null);
}

/** Human-friendly wait time, e.g. "15 minutes" or "45 seconds". */
export function formatWait(seconds: number): string {
  if (seconds <= 90) return `${Math.max(1, Math.round(seconds))} seconds`;
  const minutes = Math.ceil(seconds / 60);
  return `${minutes} minute${minutes === 1 ? "" : "s"}`;
}

/** Standard toast copy for a blocked action. */
export function throttleMessage(state: ThrottleState): { title: string; description: string } {
  return {
    title: "Too many attempts",
    description: `For your account's safety, try again in ${formatWait(state.retryAfterSeconds)}.`,
  };
}
